import { useEffectEvent, useLayoutEffect, type CSSProperties, type RefObject } from "react";

import { asPixels } from "@skilitics/designer-ui-components";

import { useEventPublish } from "../../events/index.ts";
import { useNodeStickyState } from "../../state/sticky/useNodeStickyState";
import type { CanvasStickyState } from "../canvas";
import { useLayoutConnection } from "../layoutable";
import type { LayoutSettings } from "../types";
import { propertiesEventTypes, type DimensionsUpdateEventOptions } from "./propertiesEventTypes.ts";

export type Dimensions = {
  width: CSSProperties["width"];
  height: CSSProperties["height"];
};

type DimensionsCalculatorProps = Pick<LayoutSettings, "horizontalBehavior" | "verticalBehavior"> & {
  nodeId: number;
  dimensions: Dimensions;
  domRef: RefObject<HTMLElement | null>;
};

export const DimensionsCalculator = ({
  nodeId,
  dimensions: { width, height },
  verticalBehavior,
  horizontalBehavior,
  domRef,
}: DimensionsCalculatorProps) => {
  const { layoutableId } = useLayoutConnection();
  const stickyState = useNodeStickyState<CanvasStickyState>(layoutableId);
  const publishEvent = useEventPublish();

  const recalculateDimensions = useEffectEvent(() => {
    const domElement = domRef.current;
    if (!domElement) return;

    let calculatedWidth = width;
    let calculatedHeight = height;
    if (horizontalBehavior !== "fixed") {
      const widthOfScrollbar = domElement.offsetWidth - domElement.clientWidth;
      calculatedWidth = asPixels(domElement.scrollWidth + widthOfScrollbar);
    }
    if (verticalBehavior !== "fixed") {
      const heightOfScrollbar = domElement.offsetHeight - domElement.clientHeight;
      calculatedHeight = asPixels(domElement.scrollHeight + heightOfScrollbar);
    }
    if (calculatedWidth !== width || calculatedHeight !== height) {
      // Defer callback to avoid blocking UI rendering with server updates
      setTimeout(() => {
        publishEvent(propertiesEventTypes.UPDATE_NODE_DIMENSIONS, {
          nodeId,
          width: calculatedWidth,
          height: calculatedHeight,
        } satisfies DimensionsUpdateEventOptions);
      }, 0);
    }
  });

  useLayoutEffect(() => {
    // When the horizontal/vertical behavior changes, wait for the container to render, then update the dimensions.
    recalculateDimensions();
  }, [horizontalBehavior, verticalBehavior, domRef, stickyState.width, stickyState.height]);

  return null;
};
